import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Ticket, ShoppingCart, ReceiptText, Building2, Loader2,
  UserPlus, KeyRound, CheckCircle2, Ban, Clock, BookOpen, Search, Plus, Minus, X, Lock,
  Award, Download,
} from "lucide-react";
import Topbar from "../components/Topbar";
import { api, formatApiError } from "../lib/api";
import { areaIcon, areaLabel } from "../lib/courseAreas";
import { useCart } from "../context/CartContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const eur = (v) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(v || 0));

export default function AziendaDashboard() {
  const [org, setOrg] = useState(null);
  const [learners, setLearners] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const TABS = ["discenti", "posti", "acquista", "attestati", "fatturazione", "azienda"];
  const requested = searchParams.get("tab");
  const tab = TABS.includes(requested) ? requested : "discenti";
  const setTab = (v) => setSearchParams(v === "discenti" ? {} : { tab: v }, { replace: true });

  const loadAll = useCallback(async () => {
    try {
      const [o, l, lic, ord, pay, cat, cert] = await Promise.all([
        api.get("/org"),
        api.get("/org/learners"),
        api.get("/org/licenses"),
        api.get("/org/orders"),
        api.get("/org/payments"),
        api.get("/public/catalog"),
        api.get("/org/certificates"),
      ]);
      setOrg(o.data);
      setLearners(l.data);
      setLicenses(lic.data);
      setOrders(ord.data);
      setPayments(pay.data);
      setCatalog(cat.data);
      setCertificates(cert.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Topbar />
        <div className="flex items-center justify-center py-24 text-brand-muted">
          <Loader2 className="animate-spin mr-2" size={18} /> Caricamento…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="azienda-dashboard">
      <Topbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl text-brand-ink">Area azienda</h1>
          <p className="text-sm text-brand-muted">{org?.ragione_sociale}</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6 flex flex-wrap gap-1 bg-white border border-brand-line rounded-sm p-1">
            <Tab value="discenti" icon={Users} label="Discenti" />
            <Tab value="posti" icon={Ticket} label="Posti" />
            <Tab value="acquista" icon={ShoppingCart} label="Acquista" />
            <Tab value="attestati" icon={Award} label="Attestati" />
            <Tab value="fatturazione" icon={ReceiptText} label="Fatturazione" />
            <Tab value="azienda" icon={Building2} label="Dati azienda" />
          </TabsList>

          <TabsContent value="discenti">
            <LearnersTab learners={learners} onChanged={loadAll} />
          </TabsContent>
          <TabsContent value="posti">
            <LicensesTab licenses={licenses} learners={learners} onChanged={loadAll} />
          </TabsContent>
          <TabsContent value="acquista">
            <BuyTab catalog={catalog} onChanged={loadAll} />
          </TabsContent>
          <TabsContent value="attestati">
            <CertificatesTab certificates={certificates} onChanged={loadAll} />
          </TabsContent>
          <TabsContent value="fatturazione">
            <BillingTab orders={orders} payments={payments} />
          </TabsContent>
          <TabsContent value="azienda">
            <OrgTab org={org} onChanged={loadAll} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Tab({ value, icon: Icon, label }) {
  return (
    <TabsTrigger value={value}
      className="rounded-sm data-[state=active]:bg-brand-blue data-[state=active]:text-white px-3 py-1.5 text-sm">
      <Icon size={15} className="mr-1.5" /> {label}
    </TabsTrigger>
  );
}

function Panel({ title, action, children }) {
  return (
    <div className="bg-white border border-brand-line rounded-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg text-brand-ink">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Discenti — credenziali gestite dall'azienda (username = codice fiscale)
// ---------------------------------------------------------------------------
const EMPTY_LEARNER = { fiscal_code: "", password: "", first_name: "", last_name: "", email: "", birth_date: "" };

function LearnersTab({ learners, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_LEARNER);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // discente in modifica credenziali

  const create = async (e) => {
    e.preventDefault();
    if (!form.birth_date) {
      toast.error("La data di nascita è obbligatoria (serve sull'attestato).");
      return;
    }
    setSaving(true);
    try {
      await api.post("/org/learners", {
        fiscal_code: form.fiscal_code.trim(),
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || null,
        birth_date: form.birth_date,
      });
      toast.success("Discente registrato. Comunicagli codice fiscale e password per l'accesso.");
      setOpen(false);
      setForm(EMPTY_LEARNER);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (l) => {
    try {
      await api.post(`/org/learners/${l.id}/${l.is_active ? "deactivate" : "activate"}`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Panel title="Discenti iscritti"
      action={<Button onClick={() => { setForm(EMPTY_LEARNER); setOpen(true); }} className="btn-primary h-9"><UserPlus size={15} className="mr-1.5" /> Aggiungi discente</Button>}>
      <p className="text-sm text-brand-muted -mt-2 mb-4">
        Le credenziali dei discenti le gestisci tu: lo <strong>username è il codice fiscale</strong> e la
        password la imposti qui. Comunicale al discente per l'accesso.
      </p>
      {learners.length === 0 ? (
        <Empty text="Nessun discente. Aggiungi il primo con il pulsante in alto." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead><TableHead>Username (CF)</TableHead>
              <TableHead>Stato</TableHead><TableHead>Corsi assegnati</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {learners.map((l) => (
              <TableRow key={l.id}>
                <TableCell className="font-medium">{l.first_name} {l.last_name}</TableCell>
                <TableCell className="font-mono text-xs">{l.username}</TableCell>
                <TableCell>
                  {l.is_active ? <Pill tone="green">Attivo</Pill> : <Pill tone="red">Disattivato</Pill>}
                </TableCell>
                <TableCell className="text-sm text-brand-muted">
                  {l.assigned_courses.length ? l.assigned_courses.map((c) => c.title).join(", ") : "—"}
                </TableCell>
                <TableCell className="text-right space-x-2 whitespace-nowrap">
                  <Button variant="outline" size="sm" className="h-8" onClick={() => setEditing(l)}>
                    <KeyRound size={13} className="mr-1" /> Credenziali
                  </Button>
                  <Button variant="outline" size="sm" className="h-8" onClick={() => toggle(l)}>
                    {l.is_active ? <><Ban size={13} className="mr-1" /> Disattiva</> : <><CheckCircle2 size={13} className="mr-1" /> Attiva</>}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Aggiungi un discente</DialogTitle>
            <DialogDescription>
              Lo username sarà il codice fiscale. Imposta anche la password: comunicale entrambe al discente.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Nome" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
              <Fld label="Cognome" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
            </div>
            <Fld label="Codice fiscale (username)" value={form.fiscal_code}
              onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })} required />
            <Fld label="Password (min. 8 caratteri)" type="text" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Email (facoltativa)" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Fld label="Data di nascita" type="date" value={form.birth_date}
                onChange={(e) => setForm({ ...form, birth_date: e.target.value })} required />
            </div>
            <p className="text-xs text-brand-muted -mt-2">La data di nascita è obbligatoria: serve per emettere l'attestato.</p>
            <DialogFooter>
              <Button type="submit" className="btn-primary" disabled={saving}>
                {saving ? <Loader2 className="animate-spin" size={16} /> : "Registra discente"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <EditLearnerDialog learner={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); onChanged(); }} />
    </Panel>
  );
}

function EditLearnerDialog({ learner, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!learner) { setForm(null); return; }
    setForm({
      first_name: learner.first_name || "", last_name: learner.last_name || "",
      fiscal_code: learner.fiscal_code || learner.username || "", email: learner.email || "",
      birth_date: learner.birth_date || "", password: "",
    });
  }, [learner]);
  if (!learner || !form) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        fiscal_code: form.fiscal_code.trim(),
        email: form.email.trim() || null,
        birth_date: form.birth_date || null,
      };
      if (form.password) body.password = form.password; // cambia solo se valorizzata
      await api.patch(`/org/learners/${learner.id}`, body);
      toast.success("Credenziali del discente aggiornate.");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!learner} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle>Credenziali discente</DialogTitle>
          <DialogDescription>Modifica anagrafica e credenziali. Lascia la password vuota per non cambiarla.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Nome" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
            <Fld label="Cognome" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
          </div>
          <Fld label="Codice fiscale (username)" value={form.fiscal_code}
            onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })} required />
          <Fld label="Nuova password (opzionale, min. 8)" type="text" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Lascia vuoto per non cambiarla" />
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Email (facoltativa)" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Fld label="Data di nascita" type="date" value={form.birth_date}
              onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
          </div>
          <p className="text-xs text-brand-muted -mt-2">La data di nascita serve per emettere l'attestato.</p>
          <DialogFooter>
            <Button type="submit" className="btn-primary" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={16} /> : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Posti / Licenze
// ---------------------------------------------------------------------------
function LicensesTab({ licenses, learners, onChanged }) {
  const activeLearners = learners.filter((l) => l.is_active);

  // Raggruppa i posti per corso: una scheda per corso invece di N righe.
  const groups = {};
  licenses.forEach((l) => {
    const g = groups[l.course_id] || (groups[l.course_id] = {
      course_id: l.course_id, title: l.course_title, assigned: [], available: [],
    });
    if (l.status === "assigned") g.assigned.push(l);
    else if (l.status === "available") g.available.push(l);
  });
  const courseGroups = Object.values(groups).filter((g) => g.assigned.length + g.available.length > 0);

  const unassign = async (lic) => {
    try {
      await api.delete(`/org/licenses/${lic.id}/assign`);
      toast.success("Posto liberato.");
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Panel title="Posti acquistati">
      <p className="text-sm text-brand-muted -mt-2 mb-5">
        Ogni corso mostra i posti acquistati. Spunta i discenti e assegnali tutti insieme:
        vengono iscritti automaticamente al corso.
      </p>
      {courseGroups.length === 0 ? (
        <Empty text="Nessun posto disponibile. Acquista posti dalla scheda “Acquista”." />
      ) : (
        <div className="space-y-5">
          {courseGroups.map((g) => (
            <CourseLicenses key={g.course_id} group={g} learners={activeLearners}
              onChanged={onChanged} unassign={unassign} />
          ))}
        </div>
      )}
    </Panel>
  );
}

function CourseLicenses({ group, learners, onChanged, unassign }) {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const total = group.assigned.length + group.available.length;
  const available = group.available.length;
  const assignedIds = group.assigned.map((a) => a.assigned_user_id);
  const candidates = learners.filter((l) => !assignedIds.includes(l.id));

  const toggle = (id) => {
    setSelected((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= available) return s; // non oltre i posti disponibili
      return [...s, id];
    });
  };

  const assign = async () => {
    if (!selected.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/org/licenses/assign", {
        course_id: group.course_id, user_ids: selected,
      });
      const n = data.assigned_count;
      let msg = `${n} ${n === 1 ? "discente assegnato" : "discenti assegnati"} al corso.`;
      if (data.skipped?.length) msg += ` ${data.skipped.length} non assegnati.`;
      toast.success(msg);
      setSelected([]);
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-brand-line rounded-sm bg-white p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-brand-blue/5 text-brand-blue">
            <BookOpen size={18} />
          </span>
          <div className="min-w-0">
            <h3 className="font-semibold text-brand-ink leading-snug">{group.title}</h3>
            <p className="text-xs text-brand-muted mt-0.5">
              {group.assigned.length} di {total} posti assegnati
              {available > 0 && <> · <span className="text-brand-blue font-medium">{available} disponibili</span></>}
            </p>
          </div>
        </div>
      </div>

      {/* Discenti già assegnati */}
      {group.assigned.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {group.assigned.map((a) => (
            a.started ? (
              <span key={a.id}
                title="Il discente ha avviato il corso: assegnazione bloccata"
                className="inline-flex items-center gap-1.5 text-sm bg-red-50 text-red-700 border border-red-200 rounded-sm px-2.5 py-1">
                {a.assigned_user_name || "—"}
                <Lock size={12} className="opacity-80" />
              </span>
            ) : (
              <span key={a.id}
                className="inline-flex items-center gap-1.5 text-sm bg-green-50 text-green-800 border border-green-200 rounded-sm pl-2.5 pr-1 py-1">
                {a.assigned_user_name || "—"}
                <button type="button" onClick={() => unassign(a)}
                  title="Libera il posto" aria-label="Libera il posto"
                  className="inline-flex h-5 w-5 items-center justify-center rounded-sm hover:bg-green-100 text-green-700">
                  <X size={13} />
                </button>
              </span>
            )
          ))}
        </div>
      )}
      {group.assigned.some((a) => a.started) && (
        <p className="text-[11px] text-brand-muted -mt-2 mb-4 inline-flex items-center gap-1">
          <Lock size={11} /> I discenti con il lucchetto hanno avviato il corso: l'assegnazione non è più modificabile.
        </p>
      )}

      {/* Assegnazione multipla */}
      {available === 0 ? (
        <p className="text-xs text-brand-muted border-t border-brand-line pt-3">
          Tutti i posti sono assegnati. Acquista altri posti per aggiungere discenti.
        </p>
      ) : candidates.length === 0 ? (
        <p className="text-xs text-brand-muted border-t border-brand-line pt-3">
          Tutti i discenti sono già assegnati a questo corso.
        </p>
      ) : (
        <div className="border-t border-brand-line pt-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-brand-ink">
              Assegna discenti <span className="text-brand-muted font-normal">· {selected.length}/{available} selezionati</span>
            </p>
          </div>
          <div className="max-h-52 overflow-auto rounded-sm border border-brand-line divide-y divide-brand-line">
            {candidates.map((l) => {
              const checked = selected.includes(l.id);
              const disabled = !checked && selected.length >= available;
              return (
                <label key={l.id}
                  className={`flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-brand-blue/5"}`}>
                  <input type="checkbox" checked={checked} disabled={disabled}
                    onChange={() => toggle(l.id)} className="accent-brand-blue" />
                  <span className="text-brand-ink">{l.first_name} {l.last_name}</span>
                  <span className="text-xs text-brand-muted font-mono ml-auto">{l.username}</span>
                </label>
              );
            })}
          </div>
          <div className="flex justify-end mt-3">
            <Button className="btn-primary h-9" disabled={!selected.length || busy} onClick={assign}>
              {busy ? <Loader2 className="animate-spin" size={16} />
                : <><CheckCircle2 size={15} className="mr-1.5" /> Assegna {selected.length ? `(${selected.length})` : ""}</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Acquista
// ---------------------------------------------------------------------------
const LEVEL_LABEL = { base: "Base", intermedio: "Intermedio", avanzato: "Avanzato" };

// Listino a posti lato client (identico a backend/pricing.py): serve solo per il
// preventivo istantaneo; l'ordine viene comunque ricalcolato dal server.
function seatSubtotal(price, seats) {
  let total = 0;
  for (let n = 1; n <= seats; n++) {
    const unit = n === 1 ? price : n === 2 ? Math.round(price * 90) / 100 : Math.round(price * 80) / 100;
    total = Math.round((total + unit) * 100) / 100;
  }
  return total;
}

function BuyTab({ catalog }) {
  const { addItem } = useCart();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const q = search.trim().toLowerCase();
  const filtered = catalog.filter(
    (c) => !q
      || c.title.toLowerCase().includes(q)
      || areaLabel(c.area).toLowerCase().includes(q)
  );

  return (
    <Panel title="Acquista posti">
      <p className="text-sm text-brand-muted -mt-2 mb-5">
        Scegli un corso, indica quanti posti ti servono e aggiungilo al carrello.
        Più posti acquisti, più risparmi: 1° a prezzo pieno, 2° −10%, dal 3° −20%.
      </p>

      <div className="relative max-w-sm mb-6">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca un corso o un'area…"
          className="pl-9 h-11 rounded-sm border-brand-line"
        />
      </div>

      {filtered.length === 0 ? (
        <Empty text="Nessun corso trovato." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c) => (
            <CourseBuyCard key={c.id} course={c} onBuy={() => setSelected(c)} />
          ))}
        </div>
      )}

      <PurchaseDialog
        course={selected}
        onClose={() => setSelected(null)}
        onAdd={addItem}
      />
    </Panel>
  );
}

function CourseBuyCard({ course, onBuy }) {
  const AreaIcon = areaIcon(course.area);
  return (
    <div className="group bg-white border border-brand-line rounded-sm overflow-hidden hover:shadow-md hover:border-brand-blue/40 transition-[box-shadow,border-color] duration-200 flex flex-col">
      <div className="aspect-video bg-brand-blue/5 border-b border-brand-line flex items-center justify-center overflow-hidden">
        <AreaIcon size={44} className="text-brand-blue/60" strokeWidth={1.25} />
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-widest bg-brand-blue/5 text-brand-blue border border-brand-blue/15 rounded-sm px-1.5 py-0.5">
            {areaLabel(course.area)}
          </span>
          <span className="text-[10px] uppercase tracking-widest bg-brand-gold/10 text-brand-gold-deep border border-brand-gold/30 rounded-sm px-1.5 py-0.5">
            {LEVEL_LABEL[course.level] || course.level}
          </span>
        </div>
        <h3 className="text-lg font-semibold text-brand-blue mb-2 leading-snug">{course.title}</h3>
        {course.short_description && (
          <p className="text-sm text-brand-muted leading-relaxed mb-4 line-clamp-3">{course.short_description}</p>
        )}
        <div className="mt-auto pt-4 border-t border-brand-line flex items-end justify-between gap-3">
          <div>
            {course.duration_hours != null && (
              <div className="flex items-center gap-1.5 text-xs text-brand-muted mb-1">
                <Clock size={12} /> {course.duration_hours} ore
              </div>
            )}
            <div className="text-2xl font-bold text-brand-blue">{eur(course.price)}</div>
            <div className="text-[10px] uppercase tracking-widest text-brand-muted">IVA esclusa · a posto</div>
          </div>
          <Button onClick={onBuy} className="btn-primary h-9">
            <ShoppingCart size={15} className="mr-1.5" /> Acquista
          </Button>
        </div>
      </div>
    </div>
  );
}

function PurchaseDialog({ course, onClose, onAdd }) {
  const [seats, setSeats] = useState(1);

  useEffect(() => { setSeats(1); }, [course]);
  if (!course) return null;

  const n = Math.max(1, Number(seats) || 1);
  const subtotal = seatSubtotal(course.price, n);
  const full = course.price * n;
  const saving = Math.round((full - subtotal) * 100) / 100;

  const addToCart = () => {
    onAdd(course, n);
    toast.success(`Aggiunto al carrello: ${n} ${n === 1 ? "posto" : "posti"} di “${course.title}”.`);
    onClose();
  };

  return (
    <Dialog open={!!course} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle>{course.title}</DialogTitle>
          <DialogDescription>
            {areaLabel(course.area)} · {LEVEL_LABEL[course.level] || course.level}
            {course.duration_hours != null ? ` · ${course.duration_hours} ore` : ""} · {eur(course.price)} a posto
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label className="text-sm text-brand-ink">Numero di posti</Label>
            <div className="mt-1.5 inline-flex items-center rounded-sm border border-brand-line overflow-hidden">
              <button type="button" onClick={() => setSeats((s) => Math.max(1, Number(s) - 1))}
                className="h-11 w-11 flex items-center justify-center text-brand-blue hover:bg-brand-blue/5 disabled:opacity-40"
                disabled={n <= 1} aria-label="Diminuisci">
                <Minus size={16} />
              </button>
              <input type="number" min={1} value={seats}
                onChange={(e) => setSeats(e.target.value)}
                className="h-11 w-16 text-center border-x border-brand-line outline-none" />
              <button type="button" onClick={() => setSeats((s) => Number(s) + 1)}
                className="h-11 w-11 flex items-center justify-center text-brand-blue hover:bg-brand-blue/5"
                aria-label="Aumenta">
                <Plus size={16} />
              </button>
            </div>
          </div>

          <div className="border border-brand-line rounded-sm p-4 bg-brand-blue/5">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-sm text-brand-muted">{n} {n === 1 ? "posto" : "posti"} · IVA esclusa</span>
              <span className="text-2xl font-bold text-brand-blue">{eur(subtotal)}</span>
            </div>
            {saving > 0 && (
              <p className="text-xs text-green-700">Risparmi {eur(saving)} rispetto al prezzo pieno ({eur(full)}).</p>
            )}
            <p className="text-[11px] text-brand-muted mt-1">1° a prezzo pieno · 2° −10% · dal 3° −20%</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="h-10" onClick={onClose}>Annulla</Button>
          <Button onClick={addToCart} className="btn-primary h-10">
            <ShoppingCart size={15} className="mr-1.5" /> Aggiungi al carrello · {eur(subtotal)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Fatturazione
// ---------------------------------------------------------------------------
function BillingTab({ orders, payments }) {
  return (
    <div className="space-y-6">
      <Panel title="Storico ordini">
        {orders.length === 0 ? <Empty text="Nessun ordine." /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data</TableHead><TableHead>Corsi</TableHead>
              <TableHead>Subtotale</TableHead><TableHead>Sconto</TableHead>
              <TableHead>Totale</TableHead><TableHead>Stato</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id}>
                  <TableCell className="text-sm">{fmtDate(o.created_at)}</TableCell>
                  <TableCell className="text-sm">{o.items.map((i) => `${i.course_title} ×${i.seats}`).join(", ")}</TableCell>
                  <TableCell>{eur(o.subtotal)}</TableCell>
                  <TableCell className="text-sm">{o.manual_discount ? discountLabel(o) : "—"}</TableCell>
                  <TableCell className="font-medium">{eur(o.total)}</TableCell>
                  <TableCell><OrderStatus s={o.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Panel title="Storico pagamenti">
        {payments.length === 0 ? <Empty text="Nessun pagamento registrato." /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data</TableHead><TableHead>Importo</TableHead><TableHead>Metodo</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="text-sm">{fmtDate(p.paid_at)}</TableCell>
                  <TableCell className="font-medium">{eur(p.amount)}</TableCell>
                  <TableCell className="text-sm">{p.method === "stripe" ? "Stripe" : "Amministrazione"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attestati
// ---------------------------------------------------------------------------
function CertificatesTab({ certificates }) {
  const download = async (certId) => {
    const res = await api.get(`/org/certificates/${certId}/download`, { responseType: "blob" });
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = m ? m[1] : "Attestato.pdf";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const doDownload = async (certId) => {
    try { await download(certId); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Panel title="Attestati dei discenti">
      <p className="text-sm text-brand-muted -mt-2 mb-5">
        Compaiono qui i discenti che hanno superato il test finale di un corso.
        L'attestato lo emette il discente dalla propria area; qui puoi scaricarlo
        in PDF una volta emesso, per la gestione dell'archivio.
      </p>
      {certificates.length === 0 ? (
        <Empty text="Nessun attestato. Appariranno qui i discenti che superano il test finale di un corso." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Discente</TableHead><TableHead>Corso</TableHead>
              <TableHead>Stato</TableHead><TableHead className="text-right">Azione</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {certificates.map((r) => (
              <TableRow key={r.user_id + r.course_id}>
                <TableCell className="font-medium">{r.learner_name}</TableCell>
                <TableCell className="text-sm">{r.course_title}</TableCell>
                <TableCell>
                  {r.certificate_id ? <Pill tone="green">Emesso</Pill>
                    : r.is_revoked ? <Pill tone="red">Revocato</Pill>
                    : r.missing_birth_date ? <Pill tone="amber">Dati incompleti</Pill>
                    : <Pill tone="amber">Da emettere</Pill>}
                </TableCell>
                <TableCell className="text-right">
                  {r.certificate_id ? (
                    <Button variant="outline" size="sm" className="h-8" onClick={() => doDownload(r.certificate_id)}>
                      <Download size={14} className="mr-1.5" /> Scarica
                    </Button>
                  ) : r.missing_birth_date ? (
                    <span className="text-xs text-brand-muted">Aggiungi la data di nascita nella scheda “Discenti”.</span>
                  ) : r.is_revoked ? (
                    <span className="text-xs text-brand-muted">Attestato revocato dall'amministrazione.</span>
                  ) : (
                    <span className="text-xs text-brand-muted">In attesa che il discente lo emetta.</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Dati azienda
// ---------------------------------------------------------------------------
function OrgTab({ org, onChanged }) {
  const [form, setForm] = useState({
    ragione_sociale: org.ragione_sociale || "", vat_number: org.vat_number || "",
    tax_code: org.tax_code || "", sdi_code: org.sdi_code || "", pec: org.pec || "",
    phone: org.phone || "",
    billing_address: {
      street: org.billing_address?.street || "", cap: org.billing_address?.cap || "",
      city: org.billing_address?.city || "", province: org.billing_address?.province || "",
      country: org.billing_address?.country || "IT",
    },
  });
  const [saving, setSaving] = useState(false);
  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setA = (k) => (e) => setForm({ ...form, billing_address: { ...form.billing_address, [k]: e.target.value } });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/org", form);
      toast.success("Dati azienda aggiornati.");
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Panel title="Dati azienda e fatturazione">
      <form onSubmit={save} className="max-w-2xl space-y-4">
        <Fld label="Ragione sociale" value={form.ragione_sociale} onChange={setF("ragione_sociale")} required />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Fld label="Partita IVA" value={form.vat_number} onChange={setF("vat_number")} />
          <Fld label="Codice fiscale" value={form.tax_code} onChange={setF("tax_code")} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Fld label="Codice SDI" value={form.sdi_code} onChange={setF("sdi_code")} />
          <Fld label="PEC" value={form.pec} onChange={setF("pec")} />
        </div>
        <Fld label="Indirizzo" value={form.billing_address.street} onChange={setA("street")} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Fld label="CAP" value={form.billing_address.cap} onChange={setA("cap")} />
          <Fld label="Città" value={form.billing_address.city} onChange={setA("city")} />
          <Fld label="Prov." value={form.billing_address.province} onChange={setA("province")} />
          <Fld label="Telefono" value={form.phone} onChange={setF("phone")} />
        </div>
        <Button type="submit" className="btn-primary h-10" disabled={saving}>
          {saving ? <Loader2 className="animate-spin" size={16} /> : "Salva modifiche"}
        </Button>
      </form>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Piccoli helper UI
// ---------------------------------------------------------------------------
function Fld({ label, ...props }) {
  return (
    <div>
      <Label className="text-sm text-brand-ink">{label}</Label>
      <Input {...props} className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue" />
    </div>
  );
}
function Empty({ text }) {
  return <p className="text-sm text-brand-muted py-6 text-center">{text}</p>;
}
function Pill({ tone, children }) {
  const tones = {
    green: "bg-green-50 text-green-700 border-green-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    blue: "bg-brand-blue/5 text-brand-blue border-brand-blue/20",
  };
  return <span className={`inline-block text-xs px-2 py-0.5 rounded-sm border ${tones[tone] || tones.blue}`}>{children}</span>;
}
function OrderStatus({ s }) {
  if (s === "paid") return <Pill tone="green">Pagato</Pill>;
  if (s === "cancelled") return <Pill tone="red">Annullato</Pill>;
  return <Pill tone="amber">In attesa</Pill>;
}
function discountLabel(o) {
  const d = o.manual_discount;
  if (d.pct != null) return `−${d.pct}%`;
  if (d.amount != null) return `−${eur(d.amount)}`;
  return "—";
}
function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
}
